import React, { useState, useEffect, useMemo } from 'react';
import _ from 'lodash';
import { useContextSelector } from 'use-context-selector';
import { PageQuiz } from '@scormhero/shared';

import styles from './Quiz.module.scss';
import ContainerPage from '../../UI/ContainerPage/ContainerPage';
import Button from '../../UI/Button/Button';
import Question from './Question/Question';
import Label from '../../context/Label/Label';
import { UIContext } from '../../context/UI/UIContext';
import { scrollIntoViewSmooth } from '../../utils/utils';
import QuizResult from './QuizResult';

type QuizProps = PageQuiz & {
  globalState: any;
};

function Quiz({ title, quiz = {}, globalState }: QuizProps) {
  const { questions = [], config = {} } = quiz;
  const [curQuestion, setCurQuestion] = useState(1);
  const [reviewMode, setReviewMode] = useState(false);
  const maxQuestion = questions.length;
  const [order, setOrder] = useState(
    initOrder(maxQuestion, config.randomizeQuestions)
  );
  const showAllQuestionsInPage = config.showAllQuestionsInPage === true;
  const allowRetryWhenFailed = config.allowRetryWhenFailed === true;
  const cannotProgressWhenFailed = config.cannotProgressWhenFailed === true;
  const { passingScore = 0 } = config;
  const showIntro = config.showIntro !== false;
  const showResults = config.showResults !== false;

  const {
    getTracking,
    patchTracking,
    clearTracking,
    onPageStarted,
    getCurrentPage,
    player,
  } = globalState;
  const currentPage = getCurrentPage();
  const pageTracking = getTracking(currentPage.resourceId);
  const [quizFinished, setQuizFinished] = useState(false);
  const quizStarted = pageTracking?.started;
  const result = pageTracking?.result || {};
  const setBackground = useContextSelector(UIContext, (c) => c.setBackground);
  const clearBackground = useContextSelector(
    UIContext,
    (c) => c.clearBackground
  );

  function initOrder(max: number, randomize?: boolean) {
    const inOrder = _.map(new Array(max), (a, i) => i);
    const newOrder = randomize ? _.shuffle(inOrder) : inOrder;
    return newOrder;
  }

  useEffect(() => {
    function onQuestionChange() {
      if (player?.editMode && quizFinished) {
        console.warn('[onQuestionChange]: editMode activated = reset tracking');
        handleRetry();
      }
    }
    onQuestionChange();
  }, [questions]); // eslint-disable-line

  useEffect(() => {
    function onQuestionAddOrRemove() {
      if (player?.editMode) {
        console.warn(
          '[onQuestionAddOrRemove] editMode activated = reset order and tracking'
        );
        // order need to be init, once the number of question has changed
        const newOrder = initOrder(maxQuestion, config.randomizeQuestions);
        setOrder(newOrder);
        handleRetry();
      }
    }
    onQuestionAddOrRemove();
  }, [maxQuestion]); // eslint-disable-line

  useEffect(() => {
    if (config?.background) {
      setBackground(config?.background);
    }

    return () => {
      clearBackground();
    };
  }, [config.background]); // eslint-disable-line

  useEffect(() => {
    quizFinished && calculateQuizResult();
  }, [quizFinished]); // eslint-disable-line

  // simulate click "start quiz" if showIntro config is false
  // (to avoid show this screen)
  useEffect(() => {
    if (!quizStarted && !showIntro) onPageStarted();
  }, [showIntro, quizStarted]); // eslint-disable-line

  const hasPassed = useMemo(() => {
    return +result.score >= +passingScore;
  }, [passingScore, result.score]);

  const anyChoiceTouched = useMemo(() => {
    let touched = false;
    _.forEach(questions, ({ resourceId }) => {
      if (!_.isEmpty(getTracking(resourceId))) touched = true;
    });
    return touched;
  }, [getTracking, questions]);

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement>,
    data: { value: string; type: string; questionId: string; checked?: boolean }
  ) {
    const { value, type, questionId, checked } = data;
    let { answer } = getTracking(questionId);
    if (type === 'checkbox') {
      if (!Array.isArray(answer)) answer = [];
      if (!_.includes(answer, value) && checked) {
        answer = [...answer, value];
      } else {
        answer = _.filter(answer, (a) => a !== value);
      }
    } else {
      answer = value;
    }

    // to avoid patch with undefined value
    answer ? patchTracking(questionId, { answer }) : clearTracking(questionId);
  }

  function handleSubmitAnswer() {
    const _questions = getAllQuestionsInPage();
    _.forEach(_questions, (q) => {
      const { answer } = getTracking(q.resourceId);

      let answeredCorrectly: boolean = false;

      switch (q.resourceType) {
        case 'question':
        case 'multipleChoice':
          const userAnswer = Array.isArray(answer) ? answer : [answer];
          const correctAnswer = q.choices
            .filter((c) => c.correct)
            .map((c) => c.resourceId);
          answeredCorrectly = _.isEqual(
            _.sortBy(userAnswer).toString(),
            _.sortBy(correctAnswer).toString()
          );
          break;

        case 'essay':
          const responseMatched: boolean[] = [];
          _.forEach(q.acceptableResponses, ({ text }) => {
            responseMatched.push(
              _.includes(_.lowerCase(answer), _.lowerCase(text))
            );
          });

          answeredCorrectly = q.exactResponse
            ? _.size(_.filter(responseMatched, (r) => r === true)) ===
              _.size(responseMatched)
            : !!_.find(responseMatched, (r) => r === true);

          break;

        case 'fillInTheBlank':
          let allBlanksAnsweredCorrectly = true;
          _.forEach(q.blanks, (blank, i) => {
            // warning: DO NOT use _.lowerCase (was crashing when using caps in mid string: ex: SuPeRHeRo)
            if (
              blank?.text.toLowerCase() !==
              _.get(answer, blank?.id).toLowerCase()
            )
              allBlanksAnsweredCorrectly = false;
          });
          answeredCorrectly = allBlanksAnsweredCorrectly;
          break;

        case 'matching':
        case 'ordering':
          answeredCorrectly = answer?.score === 1;
          break;

        default:
          break;
      }

      patchTracking(q.resourceId, {
        submitted: true, // mark question(s) as submitted
        answeredCorrectly,
      });
    });

    // automatically go next question (or result) if does not have feedback
    const hasFeedback =
      config?.showYourAnswers ||
      config?.showCorrectAnswer ||
      questionHasFeedback();
    if (!hasFeedback) handleNextQuestion();
  }

  function questionHasFeedback() {
    const _questions = getAllQuestionsInPage();
    return !!_questions.find((q: any) => {
      return (
        !_.isEmpty(q?.feedback?.whenCorrect) ||
        !_.isEmpty(!q?.feedback?.whenIncorrect)
      );
    });
  }

  function handleNextQuestion() {
    if (!showAllQuestionsInPage && curQuestion < maxQuestion) {
      setCurQuestion(curQuestion + 1);
    } else if (!quizFinished) {
      setQuizFinished(true);
    } else if (reviewMode) {
      setReviewMode(false);
    }
  }

  function getAllQuestionsInPage() {
    const question = questions[order[curQuestion - 1]] || {};
    const _questions = config.randomizeQuestions
      ? _.map(order, (o) => questions[o])
      : questions;
    return showAllQuestionsInPage ? _questions : [question];
  }

  function renderQuestions() {
    if ((quizFinished && !reviewMode) || !quizStarted) return;
    const _questions = getAllQuestionsInPage();
    return _.map(_questions, (q, i) => {
      const resourceId = q?.resourceId;
      const tracking = getTracking(resourceId);
      return !_.isEmpty(q) ? (
        <Question
          key={resourceId}
          question={q}
          tracking={tracking}
          config={config}
          onChange={handleChange}
          cur={showAllQuestionsInPage ? i + 1 : curQuestion}
          max={maxQuestion}
          globalState={globalState}
          className={styles.Quiz__question__component}
        />
      ) : null;
    });
  }

  function canSubmit() {
    let allQuestionsAnswered = true;

    const _questions = getAllQuestionsInPage();
    _.forEach(_questions, (q) => {
      if (_.isEmpty(getTracking(q?.resourceId)?.answer))
        allQuestionsAnswered = false;
    });

    return allQuestionsAnswered;
  }

  function canGoNextQuestion() {
    const _questions = getAllQuestionsInPage();
    let isQuestionsSubmitted = _.isEmpty(_questions) ? false : true;
    _.forEach(_questions, (q) => {
      if (!getTracking(q?.resourceId)?.submitted) isQuestionsSubmitted = false;
    });
    return isQuestionsSubmitted;
  }

  function handleReview() {
    setCurQuestion(1);
    setReviewMode(true);
  }

  function handleRetry() {
    resetPageTracking();
    setCurQuestion(1);
    setReviewMode(false);
    setQuizFinished(false);
  }

  function resetPageTracking() {
    // clear all answers (tracking) for resourceId's
    clearTracking(currentPage.resourceId);
    resetQuestionsTracking();
  }

  function resetQuestionsTracking() {
    _.forEach(questions, (q) => {
      clearTracking(q.resourceId);
    });
  }

  function calculateQuizResult() {
    // do not calculate if has already calculated
    if (!_.isEmpty(result)) return;

    // caculate result
    let totalAnsweredCorrectly = 0;
    let totalAnsweredIncorrectly = 0;
    // let totalAnsweredIncorrectly = 0;
    _.forEach(questions, ({ resourceId }) => {
      const { answeredCorrectly } = getTracking(resourceId);
      answeredCorrectly ? totalAnsweredCorrectly++ : totalAnsweredIncorrectly++;
    });

    const score = Math.round((totalAnsweredCorrectly / questions.length) * 100);
    patchTracking(currentPage.resourceId, {
      result: {
        score,
        totalAnsweredCorrectly,
        totalAnsweredIncorrectly,
        totalAnswered: totalAnsweredCorrectly + totalAnsweredIncorrectly,
      },
    });
  }

  function renderResult() {
    const { result } = pageTracking;
    if (!quizFinished || reviewMode || !result) return;

    return (
      <QuizResult
        result={result}
        hasPassed={hasPassed}
        passingScore={passingScore}
        cannotProgressWhenFailed={cannotProgressWhenFailed}
        resourceId={currentPage.resourceId}
        showResults={showResults}
        globalState={globalState}
      />
    );
  }

  function renderIntro() {
    if (quizStarted || anyChoiceTouched || !showIntro) return;
    return (
      <div className={styles.Quiz__intro}>
        <h3>
          <Label>Take this quiz to test your knowledge.</Label>
        </h3>
        <Button
          primary
          onClick={onPageStarted}
          disabled={maxQuestion === 0}
          globalState={globalState}
          testId="start-quiz-button"
          className={styles.Quiz__startButton}
        >
          <Label>start quiz</Label>
        </Button>
      </div>
    );
  }

  const canRetry = useMemo(() => {
    return quizFinished && !reviewMode && allowRetryWhenFailed && !hasPassed;
  }, [allowRetryWhenFailed, hasPassed, quizFinished, reviewMode]);

  function handleButtonOnMount(el: any) {
    if (quizFinished) return;
    scrollIntoViewSmooth(el);
  }

  return (
    <ContainerPage
      className={styles.Quiz}
      title={title}
      globalState={globalState}
    >
      {renderIntro()}
      {renderQuestions()}
      {renderResult()}
      <div className={styles.Quiz__footer}>
        {quizStarted && !quizFinished && !canGoNextQuestion() && canSubmit() && (
          <Button
            primary
            onClick={handleSubmitAnswer}
            onMount={handleButtonOnMount}
            globalState={globalState}
            testId="submit-question-button"
          >
            <Label>submit</Label>
          </Button>
        )}
        {((!quizFinished && canGoNextQuestion()) || reviewMode) && (
          <Button
            secondary
            onClick={handleNextQuestion}
            onMount={handleButtonOnMount}
            globalState={globalState}
            testId="next-question-button"
          >
            <Label>next</Label>
          </Button>
        )}
        {quizFinished && !reviewMode && !canRetry && showResults && (
          <Button secondary onClick={handleReview} globalState={globalState}>
            <Label>review</Label>
          </Button>
        )}
        {canRetry && showResults && (
          <Button secondary onClick={handleRetry} globalState={globalState}>
            <Label>retry</Label>
          </Button>
        )}
      </div>
    </ContainerPage>
  );
}

export default Quiz;
